const { useCallback, useMemo, useRef, useState } = require('react');
const { context } = require('app-context');
const {
    DEFAULT_PROFILE_IMG,
    EMPTY_PROFILE_IMG
} = require('utils/constants');
const { getOriginalImageUrl } = require('utils/image');
const { processProfileImageUploadFromEvent } = require('utils/imageUploadPresets');
const {
    captureProfilePhoto,
    fetchOriginalProfilePhotoFile,
    getProfilePhotoCaptureErrorMessage,
    validateProfilePhotoFile
} = require('utils/profilePhotoCapture');
const ProfilePhotoStateUtils = require('components/UserInputs/profilePhotoStateUtils');
const { useField } = require('./FieldContext');

const internals = {};

internals.DEFAULT_FIELD_NAMES = {
    file: 'picFile',
    previewUrl: 'picPreviewURL',
    cropPosition: 'cropPosition',
    cropRotation: 'cropRotation',
    cropScale: 'cropScale'
};

internals.cameraButtonStyle = `
    margin: 10px;
    padding: 8px 24px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
`;

internals.resolveFieldNames = (fieldNames = {}) => ({
    ...internals.DEFAULT_FIELD_NAMES,
    ...fieldNames
});

internals.isPlaceholderProfileSrc = (src) => !src
    || src === DEFAULT_PROFILE_IMG
    || src === EMPTY_PROFILE_IMG
    || src === getOriginalImageUrl(DEFAULT_PROFILE_IMG)
    || src === getOriginalImageUrl(EMPTY_PROFILE_IMG)
    || src === internals.getDefaultPreviewSrc();

internals.getDefaultPreviewSrc = () => getOriginalImageUrl(EMPTY_PROFILE_IMG || DEFAULT_PROFILE_IMG);

internals.getExistingProfileSrc = ({
    signupDetails = {}
}) => {

    const existingPicture = signupDetails.picPreviewURL
        || signupDetails.croppedPicture
        || signupDetails.picture;

    return existingPicture
        ? getOriginalImageUrl(existingPicture)
        : null;
};

internals.getPreviewSrc = ({
    values = {},
    signupDetails = {},
    rolePermissions = {}
}) => values.picPreviewURL
    || internals.getExistingProfileSrc({
        signupDetails
    })
    || internals.getDefaultPreviewSrc({
        rolePermissions
    });

internals.hasEditablePhoto = ({
    previewSrc,
    values = {},
    signupDetails = {}
}) => {

    if (values.picFile || values.picPreviewURL) {
        return true;
    }

    const existingPicture = signupDetails.picPreviewURL
        || signupDetails.croppedPicture
        || signupDetails.picture;

    return !!existingPicture
        && !internals.isPlaceholderProfileSrc(previewSrc)
        && !internals.isPlaceholderProfileSrc(existingPicture);
};

module.exports = function useProfilePhotoController({
    fieldNames
} = {}) {

    const resolvedFieldNames = internals.resolveFieldNames(fieldNames);
    const picFileField = useField(resolvedFieldNames.file);
    const picPreviewURLField = useField(resolvedFieldNames.previewUrl);
    const cropPositionField = useField(resolvedFieldNames.cropPosition);
    const cropRotationField = useField(resolvedFieldNames.cropRotation);
    const cropScaleField = useField(resolvedFieldNames.cropScale);
    const formProps = picFileField.formProps;
    const inputRef = useRef(null);
    const [cropping, setCropping] = useState(null);
    const [convertingProfilePic, setConvertingProfilePic] = useState(false);
    const [uploadingProfilePic, setUploadingProfilePic] = useState(false);

    const values = useMemo(() => ({
        picFile: picFileField.getValue(),
        picPreviewURL: picPreviewURLField.getValue(),
        cropPosition: cropPositionField.getValue(),
        cropRotation: cropRotationField.getValue(),
        cropScale: cropScaleField.getValue()
    }), [
        cropPositionField,
        cropRotationField,
        cropScaleField,
        picFileField,
        picPreviewURLField
    ]);

    const previewSrc = internals.getPreviewSrc({
        values,
        signupDetails: formProps.signupDetails,
        rolePermissions: formProps.rolePermissions
    });
    const hasEditablePhoto = internals.hasEditablePhoto({
        previewSrc,
        values,
        signupDetails: formProps.signupDetails
    });

    const setPicFile = useCallback((picFile) => {

        picFileField.setValue(picFile);

        const error = validateProfilePhotoFile(picFile);

        if (error) {
            picPreviewURLField.setValue('');
            formProps.showNotification?.(error);
            return;
        }

        setCropping(picFile);
    }, [formProps, picFileField, picPreviewURLField]);

    const uploadPhoto = useCallback((event) => {

        event?.preventDefault?.();
        inputRef.current?.click();
    }, []);

    const choosePic = useCallback(async (event) => {

        const file = ProfilePhotoStateUtils.getProfileImageUploadFileFromEvent(event);

        if (!ProfilePhotoStateUtils.shouldProcessProfileImageUpload(file)) {
            return;
        }

        const { file: processedFile, error } = await processProfileImageUploadFromEvent(event, {
            onStart: () => setConvertingProfilePic(true),
            onComplete: () => setConvertingProfilePic(false)
        });

        if (error) {
            formProps.showNotification?.('Unable to process profile photo. Please try a different image.');
        }

        setPicFile(ProfilePhotoStateUtils.getProcessedProfileImageFile({
            file,
            processedFile
        }));

        if (event?.target) {
            event.target.value = null;
        }
    }, [formProps, setPicFile]);

    const capturePhoto = useCallback(async () => {

        try {
            await captureProfilePhoto({
                cameraButtonStyle: internals.cameraButtonStyle,
                onPhoto: setPicFile
            });
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error accessing webcam:', error);
            formProps.showNotification?.(getProfilePhotoCaptureErrorMessage());
        }
    }, [formProps, setPicFile]);

    const editPhoto = useCallback(async () => {

        if (values.picFile) {
            setCropping(values.picFile);
            return;
        }

        const originalPicture = formProps.signupDetails?.picPreviewURL
            || formProps.signupDetails?.picture
            || formProps.signupDetails?.croppedPicture;

        if (!originalPicture || internals.isPlaceholderProfileSrc(originalPicture)) {
            return;
        }

        const http = context.http.nearpeer;
        const originalPicFile = await fetchOriginalProfilePhotoFile({
            http,
            originalPicture
        });

        setPicFile(originalPicFile);
    }, [formProps, setPicFile, values.picFile]);

    const closeCropping = useCallback(() => {

        const closeValues = ProfilePhotoStateUtils.buildCloseCroppingValues();

        cropPositionField.setValue(closeValues.cropPosition);
        cropRotationField.setValue(closeValues.cropRotation);
        cropScaleField.setValue(closeValues.cropScale);
        picFileField.setValue(closeValues.picFile);
        setCropping(null);
    }, [cropPositionField, cropRotationField, cropScaleField, picFileField]);

    const doneCropping = useCallback(async ({
        canvas,
        position,
        rotation,
        scale
    }) => {

        cropPositionField.setValue(position);
        cropRotationField.setValue(rotation);
        cropScaleField.setValue(scale);
        setCropping(null);

        picPreviewURLField.setValue(canvas && canvas.toDataURL());

        if (typeof formProps.saveProfilePicture !== 'function') {
            return;
        }

        setUploadingProfilePic(true);

        const result = await formProps.saveProfilePicture(ProfilePhotoStateUtils.buildSaveProfilePicturePayload({
            picFile: picFileField.getValue(),
            position,
            rotation,
            scale
        }));

        setUploadingProfilePic(false);

        if (!result) {
            const failedValues = ProfilePhotoStateUtils.buildFailedProfilePictureUploadValues();
            picPreviewURLField.setValue(failedValues.picPreviewURL);
        }
    }, [
        cropPositionField,
        cropRotationField,
        cropScaleField,
        formProps,
        picFileField,
        picPreviewURLField
    ]);

    const picFileError = picFileField.getError?.() || null;
    const profilePhotoHasError = !!picFileError;

    return {
        inputRef,
        profilePhotoRef: picFileField.inputRef,
        formProps,
        values,
        hasEditablePhoto,
        profilePhotoHasError,
        picFileError,
        state: {
            convertingProfilePic,
            cropping,
            uploadingProfilePic
        },
        actions: {
            capturePhoto,
            choosePic,
            closeCropping,
            doneCropping,
            editPhoto,
            uploadPhoto
        },
        previewSrc
    };
};

module.exports.internals = internals;
